// src/components/travel/trainUtils.js

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';

/**
 * Field configuration for train data entry
 */
export const TRAIN_FIELDS = {
    operator: {
        name: 'operator',
        label: 'Train Operator',
        type: 'select',
        required: false,
        options: () => getTrainOperatorsList(),
        allowCustom: true,
        placeholder: 'Select or enter train operator'
    },
    trainNumber: {
        name: 'trainNumber',
        label: 'Train Number',
        type: 'text',
        required: false,
        placeholder: 'e.g. IC 2345, TGV 6215',
        validation: value => validateTrainNumber(value)
    },
    departureStation: {
        name: 'departureStation',
        label: 'Departure Station',
        type: 'select',
        required: false,
        options: () => getTrainStationsList(),
        allowCustom: true,
        placeholder: 'Enter departure station'
    },
    arrivalStation: {
        name: 'arrivalStation',
        label: 'Arrival Station',
        type: 'select',
        required: false,
        options: () => getTrainStationsList(),
        allowCustom: true,
        placeholder: 'Enter arrival station'
    },
    departureTime: {
        name: 'departureTime',
        label: 'Departure Time',
        type: 'datetime',
        required: false,
        placeholder: 'Select departure date and time'
    },
    departureTimezone: {
        name: 'departureTimezone',
        label: 'Departure Timezone',
        type: 'timezone',
        required: false
    },
    arrivalTime: {
        name: 'arrivalTime',
        label: 'Arrival Time',
        type: 'datetime',
        required: false,
        placeholder: 'Select arrival date and time'
    },
    arrivalTimezone: {
        name: 'arrivalTimezone',
        label: 'Arrival Timezone',
        type: 'timezone',
        required: false
    },
    platform: {
        name: 'platform',
        label: 'Platform',
        type: 'text',
        required: false,
        placeholder: 'e.g. 5A'
    },
    coach: {
        name: 'coach',
        label: 'Coach/Car',
        type: 'text',
        required: false,
        placeholder: 'e.g. 7'
    },
    seat: {
        name: 'seat',
        label: 'Seat',
        type: 'text',
        required: false,
        placeholder: 'e.g. 42A, Window'
    },
    classOfService: {
        name: 'classOfService',
        label: 'Class',
        type: 'select',
        required: false,
        options: () => [
            { value: 'first', label: 'First Class' },
            { value: 'second', label: 'Second Class' },
            { value: 'business', label: 'Business' },
            { value: 'economy', label: 'Economy' },
            { value: 'sleeper', label: 'Sleeper' }
        ],
        allowCustom: true,
        placeholder: 'Select class of service'
    },
    bookingReference: {
        name: 'bookingReference',
        label: 'Booking Reference',
        type: 'text',
        required: false,
        placeholder: 'Booking/Reservation code'
    },
    bookingAgent: {
        name: 'bookingAgent',
        label: 'Booked Through',
        type: 'text',
        required: false,
        placeholder: 'e.g. Raileurope, Trainline'
    },
    ticketNumbers: {
        name: 'ticketNumbers',
        label: 'Ticket Number(s)',
        type: 'array',
        required: false,
        placeholder: 'Add ticket numbers'
    },
    notes: {
        name: 'notes',
        label: 'Notes',
        type: 'textarea',
        required: false,
        placeholder: 'Any additional information'
    }
};

/**
 * Common train operators
 */
const TRAIN_OPERATORS = [
    { code: 'AMTK', name: 'Amtrak' },
    { code: 'SNCF', name: 'SNCF (France)' },
    { code: 'DB', name: 'Deutsche Bahn (Germany)' },
    { code: 'ATOC', name: 'National Rail (UK)' },
    { code: 'JR', name: 'Japan Railways' },
    { code: 'RENFE', name: 'Renfe (Spain)' },
    { code: 'FS', name: 'Trenitalia (Italy)' },
    { code: 'NS', name: 'Nederlandse Spoorwegen (Netherlands)' },
    { code: 'SBB', name: 'SBB (Switzerland)' },
    { code: 'VR', name: 'VR (Finland)' },
    { code: 'SJ', name: 'SJ (Sweden)' },
    { code: 'DSB', name: 'DSB (Denmark)' },
    { code: 'NSB', name: 'NSB (Norway)' },
    { code: 'CD', name: 'České dráhy (Czech Republic)' },
    { code: 'OEBB', name: 'ÖBB (Austria)' },
    { code: 'PKP', name: 'PKP (Poland)' },
    { code: 'SNCB', name: 'SNCB (Belgium)' },
    { code: 'IR', name: 'Irish Rail' },
    { code: 'CFL', name: 'CFL (Luxembourg)' },
    { code: 'EUROSTAR', name: 'Eurostar' },
    { code: 'THALYS', name: 'Thalys' },
    { code: 'TGV', name: 'TGV' }
];

/**
 * Get the list of train operators
 * @returns {Array} List of train operators
 */
export const getTrainOperatorsList = () => {
    return TRAIN_OPERATORS.map(operator => ({
        value: operator.code,
        label: operator.name,
        code: operator.code,
        name: operator.name
    }));
};

/**
 * Validate train number format
 * @param {string} trainNumber - Train number to validate
 * @returns {Object} Validation result
 */
export const validateTrainNumber = (trainNumber) => {
    if (!trainNumber) {
        return { isValid: false, error: 'Train number is required' };
    }
    
    // Train numbers can be more varied than flight numbers
    // Accept almost any format, but trim whitespace
    const trimmed = trainNumber.trim();
    
    if (trimmed.length === 0) {
        return { isValid: false, error: 'Train number cannot be empty' };
    }
    
    return { isValid: true, parsed: trimmed };
};

/**
 * Get list of major train stations
 * This is a simplified version - in a real app, this would come from an API or database
 * @returns {Array} List of train stations
 */
export const getTrainStationsList = (cityFilter = null) => {
    const MAJOR_STATIONS = [
        // Paris stations
        { code: 'PAR-NORD', name: 'Paris Gare du Nord', city: 'Paris', cityCode: 'PAR' },
        { code: 'PAR-LYON', name: 'Paris Gare de Lyon', city: 'Paris', cityCode: 'PAR' },
        { code: 'PAR-EST', name: 'Paris Gare de l\'Est', city: 'Paris', cityCode: 'PAR' },
        { code: 'PAR-MON', name: 'Paris Montparnasse', city: 'Paris', cityCode: 'PAR' },
        
        // London stations
        { code: 'LON-STP', name: 'London St Pancras', city: 'London', cityCode: 'LON' },
        { code: 'LON-KGX', name: 'London King\'s Cross', city: 'London', cityCode: 'LON' },
        { code: 'LON-EUR', name: 'London Euston', city: 'London', cityCode: 'LON' },
        { code: 'LON-WAT', name: 'London Waterloo', city: 'London', cityCode: 'LON' },
        { code: 'LON-PAD', name: 'London Paddington', city: 'London', cityCode: 'LON' },
        
        // Berlin stations
        { code: 'BER-HBF', name: 'Berlin Hauptbahnhof', city: 'Berlin', cityCode: 'BER' },
        { code: 'BER-OST', name: 'Berlin Ostbahnhof', city: 'Berlin', cityCode: 'BER' },
        
        // Amsterdam stations
        { code: 'AMS-CS', name: 'Amsterdam Centraal', city: 'Amsterdam', cityCode: 'AMS' },
        
        // Brussels stations
        { code: 'BRU-MID', name: 'Brussels Midi/Zuid', city: 'Brussels', cityCode: 'BRU' },
        { code: 'BRU-CEN', name: 'Brussels Central', city: 'Brussels', cityCode: 'BRU' },
        
        // Rome stations
        { code: 'ROM-TER', name: 'Roma Termini', city: 'Rome', cityCode: 'ROM' },
        { code: 'ROM-TIB', name: 'Roma Tiburtina', city: 'Rome', cityCode: 'ROM' },
        
        // Madrid stations
        { code: 'MAD-ATO', name: 'Madrid Atocha', city: 'Madrid', cityCode: 'MAD' },
        { code: 'MAD-CHA', name: 'Madrid Chamartín', city: 'Madrid', cityCode: 'MAD' },
        
        // Barcelona stations
        { code: 'BCN-SANTS', name: 'Barcelona Sants', city: 'Barcelona', cityCode: 'BCN' },
        
        // Vienna stations
        { code: 'VIE-HBF', name: 'Vienna Hauptbahnhof', city: 'Vienna', cityCode: 'VIE' },
        { code: 'VIE-MWB', name: 'Vienna Westbahnhof', city: 'Vienna', cityCode: 'VIE' },
        
        // Zurich stations
        { code: 'ZRH-HB', name: 'Zürich Hauptbahnhof', city: 'Zurich', cityCode: 'ZRH' },
        
        // Geneva stations
        { code: 'GVA', name: 'Geneva Cornavin', city: 'Geneva', cityCode: 'GVA' },
        
        // Copenhagen stations
        { code: 'CPH', name: 'Copenhagen Central', city: 'Copenhagen', cityCode: 'CPH' },
        
        // Oslo stations
        { code: 'OSL-S', name: 'Oslo Central', city: 'Oslo', cityCode: 'OSL' },
        
        // Stockholm stations
        { code: 'STO-C', name: 'Stockholm Central', city: 'Stockholm', cityCode: 'STO' },
        
        // Helsinki stations
        { code: 'HEL', name: 'Helsinki Central', city: 'Helsinki', cityCode: 'HEL' },
        
        // Tokyo stations
        { code: 'TOK-TOK', name: 'Tokyo Station', city: 'Tokyo', cityCode: 'TOK' },
        { code: 'TOK-SHI', name: 'Shinagawa Station', city: 'Tokyo', cityCode: 'TOK' },
        
        // New York stations
        { code: 'NYP', name: 'New York Penn Station', city: 'New York', cityCode: 'NYC' },
        { code: 'NYG', name: 'New York Grand Central', city: 'New York', cityCode: 'NYC' },
        
        // Chicago stations
        { code: 'CHI-UNI', name: 'Chicago Union Station', city: 'Chicago', cityCode: 'CHI' }
    ];
    
    // Filter stations by city if a city filter is provided
    const filteredStations = cityFilter 
        ? MAJOR_STATIONS.filter(station => {
            // If cityFilter is a string, match against city name or code
            if (typeof cityFilter === 'string') {
                return station.city.toLowerCase() === cityFilter.toLowerCase() || 
                       station.cityCode.toLowerCase() === cityFilter.toLowerCase();
            }
            
            // If cityFilter is an object with value, label, or name properties
            if (typeof cityFilter === 'object') {
                const cityCode = cityFilter.value || '';
                const cityName = cityFilter.name || cityFilter.label || '';
                
                return station.cityCode.toLowerCase() === cityCode.toLowerCase() ||
                       station.city.toLowerCase() === cityName.toLowerCase();
            }
            
            return false;
        })
        : MAJOR_STATIONS;
    
    return filteredStations.map(station => ({
        value: station.code,
        label: station.name,
        code: station.code,
        name: station.name,
        city: station.city,
        cityCode: station.cityCode
    }));
};

/**
 * Get the city code from a city object or string
 * @param {object|string} city - City object or string
 * @returns {string} City code
 */
export const getCityCodeFromCity = (city) => {
    if (!city) return '';
    
    if (typeof city === 'object') {
        return city.value || '';
    }
    
    return city;
};

/**
 * Hook to use train fields with context
 */
export const useTrainFields = (tripId) => {
    const { currentUser } = useAuth();
    const { effectiveUserId } = useTrip();
    const [fields, setFields] = useState(TRAIN_FIELDS);

    // Initialize fields with context (user, trip)
    useEffect(() => {
        // Create a deep copy to avoid modifying the original
        const contextFields = JSON.parse(JSON.stringify(TRAIN_FIELDS));

        // For a real app, you might fetch user preferences or recent selections here

        setFields(contextFields);
    }, [currentUser, tripId, effectiveUserId]);

    return fields;
};

/**
 * Normalize train data for storage
 */
export const normalizeTrainData = (formData) => {
    const normalized = { ...formData };
    
    // Normalize train number
    if (normalized.trainNumber) {
        normalized.trainNumber = normalized.trainNumber.trim();
    }
    
    // Normalize booking link
    if (normalized.bookingLink && !normalized.bookingLink.startsWith('http')) {
        normalized.bookingLink = `https://${normalized.bookingLink}`;
    }
    
    // Ensure date fields are properly formatted ISO strings
    if (normalized.departureTime instanceof Date) {
        normalized.departureTime = normalized.departureTime.toISOString();
    }
    
    if (normalized.arrivalTime instanceof Date) {
        normalized.arrivalTime = normalized.arrivalTime.toISOString();
    }
    
    // Ensure timezone data is properly formatted
    if (normalized.departureTimezone && typeof normalized.departureTimezone === 'object') {
        normalized.departureTimezone = normalized.departureTimezone.value || normalized.departureTimezone;
    }
    
    if (normalized.arrivalTimezone && typeof normalized.arrivalTimezone === 'object') {
        normalized.arrivalTimezone = normalized.arrivalTimezone.value || normalized.arrivalTimezone;
    }
    
    return normalized;
};

/**
 * Validate train data before submission
 */
export const validateTrainData = (formData) => {
    const errors = {};
    
    // Basic validation for required fields
    if (!formData.departureStation && !formData.departureCity) {
        errors.departure = 'At least a departure city or station must be specified';
    }
    
    if (!formData.arrivalStation && !formData.arrivalCity) {
        errors.arrival = 'At least an arrival city or station must be specified';
    }
    
    // Validate time fields
    if (formData.departureTime && formData.arrivalTime) {
        // Parse dates for comparison
        const departureTime = formData.departureTime instanceof Date 
            ? formData.departureTime 
            : new Date(formData.departureTime);
        
        const arrivalTime = formData.arrivalTime instanceof Date 
            ? formData.arrivalTime 
            : new Date(formData.arrivalTime);
        
        // Check if arrival is after departure
        if (departureTime && arrivalTime && departureTime > arrivalTime) {
            errors.time = 'Arrival time cannot be earlier than departure time';
        }
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

/**
 * Infer missing fields based on existing data
 */
export const inferMissingFields = (formData) => {
    // No inference rules yet - would be added based on business logic
    return formData;
};

/**
 * Map of common city codes to IANA timezone strings
 */
export const CITY_TIMEZONES = {
    // Europe
    'LON': 'Europe/London',        // London
    'PAR': 'Europe/Paris',         // Paris
    'BER': 'Europe/Berlin',        // Berlin
    'MAD': 'Europe/Madrid',        // Madrid
    'ROM': 'Europe/Rome',          // Rome
    'AMS': 'Europe/Amsterdam',     // Amsterdam
    'BRU': 'Europe/Brussels',      // Brussels
    'ZRH': 'Europe/Zurich',        // Zurich
    'VIE': 'Europe/Vienna',        // Vienna
    'CPH': 'Europe/Copenhagen',    // Copenhagen
    'OSL': 'Europe/Oslo',          // Oslo
    'STO': 'Europe/Stockholm',     // Stockholm
    'HEL': 'Europe/Helsinki',      // Helsinki
    
    // Asia
    'TOK': 'Asia/Tokyo',           // Tokyo
    
    // North America
    'NYC': 'America/New_York',     // New York
    'CHI': 'America/Chicago',      // Chicago
};

/**
 * Get the timezone for a city
 * @param {object|string} city - City object or string
 * @returns {object|null} Timezone object for TimezoneSelect or null if not found
 */
export const getTimezoneForCity = (city) => {
    if (!city) return null;
    
    let cityCode = '';
    
    if (typeof city === 'object') {
        cityCode = city.value || '';
        
        // If there's no value, try to match by name
        if (!cityCode && (city.name || city.label)) {
            const cityName = (city.name || city.label).toUpperCase();
            
            if (cityName === 'LONDON') return { value: 'Europe/London', label: 'London (GMT/BST)' };
            if (cityName === 'PARIS') return { value: 'Europe/Paris', label: 'Paris (CET/CEST)' };
            if (cityName === 'BERLIN') return { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' };
            if (cityName === 'TOKYO') return { value: 'Asia/Tokyo', label: 'Tokyo (JST)' };
            if (cityName === 'NEW YORK') return { value: 'America/New_York', label: 'New York (EST/EDT)' };
        }
    } else {
        cityCode = city.toUpperCase();
    }
    
    const timezone = CITY_TIMEZONES[cityCode];
    
    if (!timezone) return null;
    
    // Format the timezone for display
    const timezoneLabels = {
        'Europe/London': 'London (GMT/BST)',
        'Europe/Paris': 'Paris (CET/CEST)',
        'Europe/Berlin': 'Berlin (CET/CEST)',
        'Europe/Madrid': 'Madrid (CET/CEST)',
        'Europe/Rome': 'Rome (CET/CEST)',
        'Europe/Amsterdam': 'Amsterdam (CET/CEST)',
        'Europe/Brussels': 'Brussels (CET/CEST)',
        'Europe/Zurich': 'Zurich (CET/CEST)',
        'Europe/Vienna': 'Vienna (CET/CEST)',
        'Europe/Copenhagen': 'Copenhagen (CET/CEST)',
        'Europe/Oslo': 'Oslo (CET/CEST)',
        'Europe/Stockholm': 'Stockholm (CET/CEST)',
        'Europe/Helsinki': 'Helsinki (EET/EEST)',
        'Asia/Tokyo': 'Tokyo (JST)',
        'America/New_York': 'New York (EST/EDT)',
        'America/Chicago': 'Chicago (CST/CDT)'
    };
    
    return {
        value: timezone,
        label: timezoneLabels[timezone] || timezone
    };
}; 